import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Table, Input, Badge } from 'reactstrap';
import ZapmailOrderSidebar from './ZapmailOrderSidebar';
import { getZapmailBilling, lookupZapmailWorkspace } from '../../../store/Admin';

// Domain & Email Infrastructure (Done-For-You) — every DFY order across workspaces with our
// Zapmail cost, what the customer paid, profit, comped orders flagged, live wallet balance and
// the platform's provisioned-vs-BYO mailbox footprint. Rows open a right slider with full details.
const ZapmailOrders = () => {
    const dispatch = useDispatch();
    const { zapmailBilling, zapmailBillingLoading } = useSelector(state => ({
        zapmailBilling: state.Admin.zapmailBilling || { orders: [], stats: {}, pagination: {} },
        zapmailBillingLoading: state.Admin.zapmailBillingLoading
    }));
    const [statusFilter, setStatusFilter] = useState('all');
    const [page, setPage] = useState(1);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [lookup, setLookup] = useState({ query: '', searching: false, results: null });

    useEffect(() => {
        dispatch(getZapmailBilling({ page, limit: 25, status: statusFilter === 'all' ? undefined : statusFilter }));
    }, [dispatch, page, statusFilter]);

    // Reverse-lookup: paste a Zapmail workspace id (sellagent-<number>) from their dashboard to find the client
    const runLookup = () => {
        const query = lookup['query'].trim();
        if (!query || lookup['searching']) return;
        setLookup(prev => ({ ...prev, searching: true }));
        dispatch(lookupZapmailWorkspace(query, (results) => setLookup(prev => ({ ...prev, searching: false, results }))));
    };
    const clearLookup = () => setLookup({ query: '', searching: false, results: null });

    const { orders, stats, pagination } = zapmailBilling;
    const infrastructure = stats['infrastructure'] || {};
    const profit = stats['profit'] || 0;

    return (
        <div className='tabs-area zapmail-admin'>
            <div className='zapmail-admin-head'>
                <h4>Domain &amp; Email Infrastructure</h4>
                <p>Done-For-You domains and mailboxes sold to workspaces — revenue, provider cost and margin</p>
            </div>

            <div className='zapmail-admin-lookup'>
                <div className='zapmail-admin-lookup-field'>
                    <i className='fa fa-search'></i>
                    <input type='text' placeholder='Find a client by Zapmail workspace id (e.g. sellagent-99782682) or name' value={lookup['query']}
                        onChange={e => setLookup(prev => ({ ...prev, query: e['target']['value'] }))}
                        onKeyDown={e => { if (e['key'] === 'Enter') runLookup(); }} />
                    {lookup['results'] !== null && <button className='zapmail-admin-lookup-clear' onClick={clearLookup} title='Clear'><i className='fa fa-times'></i></button>}
                </div>
                <button className='btn-style-new' onClick={runLookup} disabled={lookup['searching'] || !lookup['query'].trim()}>
                    {lookup['searching'] ? <i className='fa fa-spinner fa-spin'></i> : 'Look up'}
                </button>
            </div>
            {lookup['results'] !== null && (
                <div className='zapmail-admin-lookup-results'>
                    {!lookup['results'].length ? (
                        <p className='zapmail-admin-lookup-empty'>No workspace matches “{lookup['query']}”.</p>
                    ) : lookup['results'].map(result => (
                        <div key={result['workspaceId']} className='zapmail-admin-lookup-card'>
                            <div className='zapmail-admin-lookup-main'>
                                <strong>{result['name']}</strong>
                                <span className='zapmail-admin-lookup-zid'>{result['zapmailWorkspaceId'] ? `sellagent-${result['zapmailWorkspaceId']}` : 'not provisioned yet'}</span>
                            </div>
                            <div className='zapmail-admin-lookup-meta'>
                                {result['owner'] && <span>{result['owner']['name'] || result['owner']['email']}</span>}
                                <span>{result['domains']} domain{result['domains'] === 1 ? '' : 's'}</span>
                                <span>{result['mailboxes']} mailbox{result['mailboxes'] === 1 ? '' : 'es'}</span>
                                <span>{result['orders']} order{result['orders'] === 1 ? '' : 's'}</span>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <div className='counter-sections'>
                <div className='counter-box style-two'>
                    <div className='counter-start'>
                        <div className='left-text-area'>
                            <h5>Profit</h5>
                            <div className='CountUp-box'><h3 className={profit >= 0 ? 'text-success' : 'text-danger'}>{formatCents(profit)}</h3></div>
                            <small>revenue minus Zapmail cost</small>
                        </div>
                        <div className='right-icon'><span className='icon'><i className={`fa fa-bar-chart ${profit >= 0 ? 'text-success' : 'text-danger'}`}></i></span></div>
                    </div>
                </div>
                <div className='counter-box style-two'>
                    <div className='counter-start'>
                        <div className='left-text-area'>
                            <h5>Customer Revenue</h5>
                            <div className='CountUp-box'><h3 className='text-success'>{formatCents(stats['revenue'])}</h3></div>
                            <small>{stats['orders'] || 0} paid order{(stats['orders'] || 0) === 1 ? '' : 's'}</small>
                        </div>
                        <div className='right-icon'><span className='icon'><i className='fa fa-usd text-success'></i></span></div>
                    </div>
                </div>
                <div className='counter-box style-two'>
                    <div className='counter-start'>
                        <div className='left-text-area'>
                            <h5>Zapmail Cost</h5>
                            <div className='CountUp-box'><h3 className='text-warning'>{formatCents(stats['cost'])}</h3></div>
                            <small>{stats['compedCount'] || 0} admin-comped ({formatCents(stats['compedCost'])})</small>
                        </div>
                        <div className='right-icon'><span className='icon'><i className='fa fa-shopping-cart text-warning'></i></span></div>
                    </div>
                </div>
                <div className='counter-box style-two'>
                    <div className='counter-start'>
                        <div className='left-text-area'>
                            <h5>Zapmail Wallet</h5>
                            <div className='CountUp-box'><h3>{stats['walletBalance'] == null ? '—' : `$${Number(stats['walletBalance']).toLocaleString()}`}</h3></div>
                            <small>{stats['autoRechargeEnabled'] == null ? 'live balance' : `auto-recharge ${stats['autoRechargeEnabled'] ? 'on' : 'off'}`}</small>
                        </div>
                        <div className='right-icon'><span className='icon'><i className='fa fa-credit-card'></i></span></div>
                    </div>
                </div>
            </div>

            <div className='zapmail-admin-infra'>
                <div className='zapmail-admin-infra-item'>
                    <span className='zapmail-admin-infra-icon'><i className='fa fa-globe'></i></span>
                    <div className='zapmail-admin-infra-text'>
                        <strong>{infrastructure['domains'] ?? '—'}</strong>
                        <span>domains purchased through us</span>
                    </div>
                </div>
                <div className='zapmail-admin-infra-item'>
                    <span className='zapmail-admin-infra-icon'><i className='fa fa-envelope'></i></span>
                    <div className='zapmail-admin-infra-text'>
                        <strong>{infrastructure['dfyMailboxes'] ?? '—'}</strong>
                        <span>Done-For-You mailboxes provisioned</span>
                    </div>
                </div>
                <div className='zapmail-admin-infra-item'>
                    <span className='zapmail-admin-infra-icon'><i className='fa fa-plug'></i></span>
                    <div className='zapmail-admin-infra-text'>
                        <strong>{infrastructure['byoMailboxes'] ?? '—'}</strong>
                        <span>customer-connected mailboxes (not from us)</span>
                    </div>
                </div>
            </div>

            <div className='table-container filters-table'>
                <div className='header-table-filters advanced-table-filters zapmail-admin-table-head'>
                    <div className='title-area style-workshop'><h4>Done-For-You Orders</h4></div>
                    <Input type='select' value={statusFilter} onChange={e => { setStatusFilter(e['target']['value']); setPage(1); }} className='zapmail-admin-status-filter'>
                        {STATUS_OPTIONS.map(option => <option key={option} value={option}>{option === 'all' ? 'All statuses' : STATUS_LABELS[option] || option}</option>)}
                    </Input>
                </div>
                <div className='table-area'>
                    {zapmailBillingLoading ? (
                        <div className='admin-tab-loader'><i className='fa fa-spinner fa-spin'></i></div>
                    ) : !(orders || []).length ? (
                        <div className='notfound-area'><p>No Done-For-You orders yet</p></div>
                    ) : (
                        <Table responsive hover className='zapmail-admin-table'>
                            <thead>
                                <tr>
                                    <th>Workspace</th>
                                    <th>Placed By</th>
                                    <th>Date</th>
                                    <th>Items</th>
                                    <th>Status</th>
                                    <th className='zapmail-admin-num'>Customer Paid</th>
                                    <th className='zapmail-admin-num'>Our Cost</th>
                                    <th className='zapmail-admin-num'>Profit</th>
                                </tr>
                            </thead>
                            <tbody>
                                {orders.map(order => {
                                    const retail = order['compedByAdmin'] ? 0 : (order['totals']?.['retail'] || 0);
                                    const cost = order['totals']?.['cost'] || 0;
                                    return (
                                        <tr key={order['_id']} className='zapmail-admin-row' onClick={() => setSelectedOrder(order)}>
                                            <td>
                                                <div className='zapmail-admin-ws'>{order['workspaceId']?.['name'] || '—'}</div>
                                                {order['workspaceId']?.['zapmail']?.['workspaceId'] && <div className='zapmail-admin-ws-zid'>sellagent-{order['workspaceId']['zapmail']['workspaceId']}</div>}
                                            </td>
                                            <td>{userName(order['userId'])}</td>
                                            <td>{new Date(order['createdAt']).toLocaleDateString()}</td>
                                            <td>{itemsSummary(order)}</td>
                                            <td>
                                                <Badge color={STATUS_COLORS[order['status']] || 'secondary'}>{STATUS_LABELS[order['status']] || order['status']}</Badge>
                                                {order['compedByAdmin'] && <Badge color='info' className='ml-1'>Admin comped</Badge>}
                                            </td>
                                            <td className='zapmail-admin-num'>{order['compedByAdmin'] ? '—' : formatCents(order['totals']?.['retail'])}</td>
                                            <td className='zapmail-admin-num'>{formatCents(cost)}</td>
                                            <td className={`zapmail-admin-num ${retail - cost >= 0 ? 'text-success' : 'text-danger'}`}>{formatCents(retail - cost)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    )}
                </div>
                {(pagination?.['pages'] || 0) > 1 && (
                    <div className='zapmail-admin-pagination'>
                        <button className='btn-style-border' disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</button>
                        <span className='zapmail-admin-pagination-info'>Page {pagination['page']} of {pagination['pages']}</span>
                        <button className='btn-style-border' disabled={page >= pagination['pages']} onClick={() => setPage(page + 1)}>Next</button>
                    </div>
                )}
            </div>

            <ZapmailOrderSidebar isOpen={Boolean(selectedOrder)} order={selectedOrder} onClose={() => setSelectedOrder(null)} />
        </div>
    );
};

const STATUS_OPTIONS = ['all', 'paid', 'provisioning', 'completed', 'partially_failed', 'failed', 'refunded', 'pending_payment'];
const STATUS_LABELS = { paid: 'Paid', provisioning: 'Provisioning', completed: 'Completed', partially_failed: 'Partially Failed', failed: 'Failed', refunded: 'Refunded', pending_payment: 'Pending Payment', canceled: 'Canceled' };
const STATUS_COLORS = { paid: 'primary', provisioning: 'info', completed: 'success', partially_failed: 'warning', failed: 'danger', refunded: 'secondary', pending_payment: 'warning', canceled: 'secondary' };

// Order amounts are stored in cents
const formatCents = (cents) => `$${((cents || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const userName = (user) => user && typeof user === 'object' ? (`${user['firstName'] || ''} ${user['lastName'] || ''}`.trim() || user['email'] || '—') : '—';

const itemsSummary = (order) => {
    const domains = (order['items'] || []).filter(item => item['kind'] === 'domain').length;
    const mailboxes = (order['items'] || []).filter(item => item['kind'] === 'mailbox_plan').reduce((count, item) => count + (item['mailboxes']?.length || 0), 0);
    const parts = [];
    if (domains) parts.push(`${domains} domain${domains === 1 ? '' : 's'}`);
    if (mailboxes) parts.push(`${mailboxes} mailbox${mailboxes === 1 ? '' : 'es'}`);
    return parts.join(', ') || '—';
};

export default ZapmailOrders;
